import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ButtonLink } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { BodyText, EditorialHeader, InlineNotice, Overline, PageSection, SectionTitle, TextLink } from '@components/common/Editorial';
import { Price, TaxExclusion } from '@components/product/Price';
import { ProductMedia } from '@components/product/ProductMedia';
import { ProductPurchase } from '@components/product/ProductPurchase';
import { PlanBenefits } from '@components/product/PlanBenefits';
import { PlanComparison } from '@components/product/PlanComparison';
import { getProductBySlug, products } from '@data/products';
import { usePageTitle } from '@hooks/usePageTitle';
import { isProductAvailable } from '@app-types/product';
export const ProductDetailPage = () => {
    const { slug } = useParams();
    const { t } = useTranslation();
    const product = slug ? getProductBySlug(slug) : undefined;
    usePageTitle(product?.nameKey ?? 'productsPage.notFound');
    if (!product)
        return (_jsx(EditorialHeader, { eyebrow: t('productsPage.title'), title: t('productsPage.notFound'), intro: t('productsPage.notFoundText'), backTo: "/products", backLabel: t('interior.product.back'), children: _jsx(ButtonLink, { to: "/products", children: t('actions.viewProducts') }) }));
    const software = product.category === 'software';
    const related = products.filter((item) => item.id !== product.id);
    return (_jsxs(_Fragment, { children: [_jsxs(EditorialHeader, { eyebrow: `${t('productsPage.title')} / ${t(`productsPage.categories.${product.category}`)}`, title: t(product.nameKey), intro: t(product.shortDescriptionKey), backTo: "/products", backLabel: t('interior.product.back'), children: [_jsx(StateLabel, { children: t(product.availability === 'coming_soon'
                            ? 'availability.comingSoon'
                            : product.id === 'personal-care-360'
                                ? 'personalCare360.premium'
                                : product.variants
                                    ? 'planComparison.eyebrow'
                                    : product.commercial
                                        ? 'productsPage.commercialOffer'
                                        : 'home.software.demo') }), _jsx(TypeLabel, { children: software ? t('home.software.eyebrow') : t('interior.product.service') })] }), _jsx(DetailSection, { "$software": software, "aria-labelledby": "product-about-title", children: _jsxs(DetailGrid, { children: [_jsxs(ImageColumn, { children: [_jsx(ProductMedia, { product: product, eager: true, caption: true }), _jsx(ImageSignature, { "aria-hidden": "true", children: "AI + Technology + Care" })] }), _jsxs("div", { children: [_jsx(Overline, { children: software
                                        ? t('interior.product.softwareEyebrow')
                                        : t('interior.product.serviceEyebrow') }), _jsx(SectionTitle, { id: "product-about-title", children: product.variants
                                        ? t(product.shortDescriptionKey)
                                        : software
                                            ? t('interior.product.softwareTitle')
                                            : t('interior.product.serviceTitle') }), _jsx(BodyText, { children: t(product.descriptionKey) }), product.availability === 'coming_soon' ? (_jsxs(DetailNotice, { children: [_jsx("strong", { children: t('availability.comingSoon') }), _jsx("p", { children: t('availability.inPreparation') })] })) : !product.variants && !product.commercial ? (_jsxs(DetailNotice, { children: [_jsx("strong", { children: t('home.software.demo') }), _jsx("p", { children: t('home.showcase.catalogNote') })] })) : null, _jsx(PurchaseBlock, { product: product }, product.id), !software && (_jsxs(Assistance, { children: [_jsx("p", { children: t('interior.product.askText') }), _jsxs(TextLink, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 17, "aria-hidden": "true" })] })] }))] })] }) }), product.id === 'personal-care-360' && _jsx(PlanBenefits, {}), product.category === 'support' && _jsx(PlanComparison, {}), _jsx(PageSection, { "aria-labelledby": "related-title", children: _jsxs(Container, { children: [_jsxs(RelatedHeader, { children: [_jsxs("div", { children: [_jsx(Overline, { children: t('interior.product.keepExploring') }), _jsx(SectionTitle, { id: "related-title", children: t('interior.product.related') })] }), _jsxs(TextLink, { to: "/products", children: [_jsx(ArrowLeft, { size: 17, "aria-hidden": "true" }), t('interior.product.back')] })] }), _jsx(RelatedList, { children: related.map((item) => (_jsx("li", { children: _jsxs(TextLink, { to: `/products/${item.slug}`, children: [_jsxs("div", { children: [_jsx("span", { children: t(`productsPage.categories.${item.category}`) }), _jsx("strong", { children: t(item.nameKey) })] }), _jsxs(RelatedPrice, { children: [isProductAvailable(item) ? (_jsxs(_Fragment, { children: [item.variants && _jsx("span", { children: t('personalCare360.from') }), _jsx(Price, { amountMinor: item.priceMinor, currency: item.currency }), _jsx(TaxExclusion, { short: true })] })) : (_jsx(RelatedAvailability, { children: t('availability.comingSoon') })), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] }) }, item.id))) })] }) })] }));
};
const StateLabel = styled.span `
  display: inline-flex;
  padding: 0.3rem 0.6rem;
  background: ${({ theme }) => theme.colors.mint50};
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.8125rem;
  line-height: 1.5;
`;
const TypeLabel = styled.span `
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.8125rem;
`;
const DetailSection = styled(PageSection) `
  background: ${({ $software, theme }) => ($software ? theme.colors.surface : theme.colors.white)};
  padding-top: 1rem;
`;
const DetailGrid = styled(Container) `
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  gap: clamp(3rem, 7vw, 7rem);
  @media (max-width: 899px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2.5rem;
  }
`;
const ImageColumn = styled.div `
  align-self: start;
  padding-top: 0.5rem;
  @media (min-width: 900px) {
    position: sticky;
    top: 8rem;
  }
`;
const ImageSignature = styled.div `
  max-width: 480px;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  margin-top: 1.5rem;
  padding-top: 1rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.6875rem;
  letter-spacing: 0.1em;
`;
const DetailNotice = styled(InlineNotice) `
  margin-top: 1.5rem;
`;
const PurchaseBlock = styled(ProductPurchase) `
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.navy900};
`;
const Assistance = styled.div `
  margin-top: 1.5rem;
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.9375rem;
    line-height: 1.65;
  }
`;
const RelatedHeader = styled.div `
  display: flex;
  align-items: end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
  ${SectionTitle} {
    font-size: clamp(1.6rem, 2.3vw, 2rem);
  }
`;
const RelatedList = styled.ul `
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  li {
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  }
  a {
    display: flex;
    justify-content: space-between;
    gap: 2rem;
    width: 100%;
    padding-block: 1.25rem;
  }
  a > div {
    display: grid;
    gap: 0.3rem;
    min-width: 0;
  }
  a > div > span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
    font-weight: 400;
  }
  a strong {
    font-size: 1rem;
    line-height: 1.5;
  }
  @media (max-width: 374px) {
    a {
      flex-wrap: wrap;
      gap: 0.75rem;
    }
  }
`;
const RelatedPrice = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  white-space: nowrap;
  font-size: 0.9375rem;
`;
const RelatedAvailability = styled.span `
  color: ${({ theme }) => theme.colors.navy900};
  font-weight: 700;
  white-space: normal;
`;
